import React, { ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import classNames from 'classnames';

import { useRerender } from '@hcs/hooks';

import {
  Button,
  ButtonProps,
} from '../../../controls-and-inputs/buttons/Button';

import styles from './ActionButtons.module.css';

export interface ActionButtonProps {
  dataHcName?: string;
  className?: string;
  // Buttons to Render
  actions?: ((ButtonProps & { linkTo?: string }) | null)[];
  // ID of Another container to render the buttons in (such as dialog actions)
  portalIdRender?: string;
  children?: ReactNode;
  /**
   * Prevent the buttons from stretching to fill the container
   */
  noStretch?: boolean;
  direction?: 'row' | 'column';
}

export const ActionButtons = ({
  dataHcName,
  className,
  actions,
  portalIdRender,
  children,
  noStretch,
  direction,
}: ActionButtonProps) => {
  const elm = portalIdRender
    ? document.querySelector(`#${portalIdRender}`)
    : undefined;
  const renderedActions = (
    <div
      data-hc-name={dataHcName}
      className={classNames(styles.ActionButtons, className, {
        [styles.noStretch]: noStretch,
        [styles.column]: direction === 'column',
      })}
    >
      {actions?.map((b, i) => {
        if (!b) return null;
        const { linkTo, ...buttonProps } = b;
        const button = (
          <Button
            {...buttonProps}
            key={`action-${i}-${buttonProps.dataHcName}`}
          />
        );
        if (linkTo) {
          return <Link to={linkTo}>{button}</Link>;
        }
        return button;
      })}
      {children}
    </div>
  );
  // Re-render once if the target element isn't rendered
  // This is needed when this feature and the portal mount simultaneously
  useRerender({
    deps: [portalIdRender],
    shouldRerender: !!portalIdRender && !elm,
    max: 1,
  });

  if (portalIdRender) {
    if (elm) {
      return createPortal(renderedActions, elm);
    } else {
      return null;
    }
  } else {
    return renderedActions;
  }
};
